import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import StartsStopsView from "../map/StartsStopsView";
import { useSelector } from "react-redux";
import {ThemeProvider} from "@material-ui/core/styles";
import {createAppTheme} from "../theme";

const StartsStopsGoogleMap = React.memo(({ map, googleMapsApi, positions, stops }) => {

    const devices = useSelector(state => state.devices.items);
    const [popup, setPopup] = useState({pos: null, status: null, deviceName: null});
    const infoWindowRef = useRef(null);
    const markerRef = useRef({start: null, end: null, parking: []});
    const mapType = useSelector(state => state.session.user && state.session.user.map);
    const mode = useSelector(state => state.theme.mode);
    const theme = useMemo(() => createAppTheme(mode), [mode]);

    const deviceName = useCallback((deviceId) => devices[deviceId]?.name,[devices])

    const clearMarkers = useCallback(() => {
        if (markerRef.current.start){
            markerRef.current.start.setMap(null);
            markerRef.current.start = null;
        }
        if (markerRef.current.end){
            markerRef.current.end.setMap(null);
            markerRef.current.end = null;
        }
            markerRef.current.parking.forEach(m => m.marker.setMap(null))
            markerRef.current.parking = []
    }, []);

    // This effect handles the creation of Start and End markers
    useEffect(() => {
        if (!map || !googleMapsApi || !positions || positions.length === 0) return;

        clearMarkers();

        if (map && googleMapsApi && !infoWindowRef.current) {
            infoWindowRef.current = new googleMapsApi.InfoWindow();
        }

        //close InfoWindow (card details) when click on map.
        const mapClickListener = map.addListener('click', () => {
            if (infoWindowRef.current)
                infoWindowRef.current.close();
        })

        const startStopPositions = [positions[0], positions[positions.length - 1]];
        //open popup info card of Start or End; if click anywhere far even if parking position, close it.
        startStopPositions.forEach((position, index) => {
            const positionLatLon = new googleMapsApi.LatLng(position.latitude, position.longitude);
            const marker = new googleMapsApi.Marker({
                position: positionLatLon,
                map,
                icon: {
                    url: index === 0 ? 'images/start.png' : 'images/end.png',
                    scaledSize: new googleMapsApi.Size(25, 35)
                },
                title: index === 0 ? "Start" : "End",
                deviceId: position.deviceId,
            });
            const status = index === 0 ? "Start" : "End";
            if (index === 0) {
                markerRef.current.start = marker;
            } else {
                markerRef.current.end = marker;
            }
            marker.addListener('click', () => setPopup({pos: position, status: status, deviceName: deviceName(position.deviceId)}));
        });

        //open popup info card of parking position; if click anywhere far even if Start or End position, close it.
        stops.forEach(parking => {
            const { longitude, latitude } = parking;
            const positionLatLon = new googleMapsApi.LatLng(latitude, longitude);
            const marker = new googleMapsApi.Marker({
                position: positionLatLon,
                map,
                icon: {
                    url: parking.duration < 120000 ? 'images/parkingOrg.png' : 'images/parkingRed.png',
                    scaledSize: new googleMapsApi.Size(20, 30)
                },
                title: "Parking",
            });
            marker.addListener('click', () => setPopup({pos: parking}));
            markerRef.current.parking.push({ parking, marker });
        })

        // Cleanup on component unmount or when dependencies change
        return () => {
            googleMapsApi.event.removeListener(mapClickListener);
            clearMarkers();
            if (infoWindowRef.current) infoWindowRef.current.close();
        }
    }, [map, positions, stops, mode]);

    useEffect(() => {
        if(popup.pos){
            const placeHolder = document.createElement('div');
            ReactDOM.render(
                <ThemeProvider theme={theme}>
                    <StartsStopsView position={popup.pos} status={popup.status} deviceName={popup.deviceName} mapType={mapType}/>
                </ThemeProvider>
                , placeHolder);
            if (!map || !markerRef.current) return;

            let marker = null;
            if (popup.status === "Start") {
                marker = markerRef.current.start;
            } else if (popup.status === "End") {
                marker = markerRef.current.end;
            } else {
                const found = markerRef.current.parking.find(m => m.parking === popup.pos);
                marker = found?.marker;
            }
            if (marker) {
                infoWindowRef.current.setContent(placeHolder);
                infoWindowRef.current.open(map, marker);
            }
        }
    }, [popup, map]);

    useEffect(() => {
        const styleId = 'google-maps-cyber-popup';
        let styleElement = document.getElementById(styleId);

        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        const isDarkMode = theme.palette.typeBackground === 'dark';
        const popupBg = isDarkMode ? 'rgba(15, 25, 35, 0.7)' : 'rgba(255, 255, 255, 0.5)';
        const glowOpacity = isDarkMode ? '0.4' : '0.2';

        styleElement.innerHTML = `
    /* Main wrapper container override */
    .gm-style-iw-c {
      background: ${popupBg} !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      border: 1px solid #00f2ff !important;
      box-shadow: 0 0 20px rgba(0, 242, 255, ${glowOpacity}) !important;
      border-radius: 12px !important;
      padding: 0 !important;
    }

    /* Target inner content block container */
    .gm-style-iw-d {
      overflow: hidden !important;
      padding: 0 !important;
      max-height: none !important;
    }

    /* Google Maps native close button container */
    .gm-style-iw-chr {
      position: absolute !important;
      right: 0;
      top: 0;
      z-index: 1;
    }

    /* Close Button Wrapper */
     .gm-style-iw-chr > button.gm-ui-hover-effect {
      flex: 0 0 50% !important; /* Combines grow, shrink, and basis */
      height: 15px !important;
      margin: 4px 4px 0 0 !important;
      padding: 4px !important;
      outline: none !important;
      color: #00f2ff !important;
      opacity: 0.8 !important;
      transition: background-color 0.2s ease;
    }
   /* Close Button Hover */
    .gm-style-iw-chr > button.gm-ui-hover-effect:hover {
     background-color: rgba(0, 242, 255, 0.1) !important;
     border-radius: 50% !important;
     opacity: 1 !important;
    }
    
    /* Inner 'X' Icon Graphic */
    .gm-style-iw-chr > button.gm-ui-hover-effect > span {
     width: 100% !important;
     height: 100% !important;
     margin: 0 !important;
     background-color: #00f2ff !important;
     mask-repeat: no-repeat !important;
     mask-position: right !important; 
     }
    
    /* Target the popup tail/arrow pointer */
    .gm-style-iw-tc::after {
      background: ${popupBg} !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      border: 1px solid #00f2ff !important;
      /* Mask border rules so it seamlessly joins the box border */
      border-top: none !important;
      border-left: none !important;
      box-shadow: 7px 7px 15px rgba(0, 242, 255, ${glowOpacity}) !important;
    }
  `;

        return () => {
            // Optional: if (styleElement) styleElement.remove();
        };
    }, [theme]);

    return null;
});

export default StartsStopsGoogleMap;